import React, { useEffect, useRef, useState } from 'react';
import { mediaService, type MediaAsset } from '../../services/studio.service';
import { BrandBtn, Modal, Spinner } from '../../components/studio/studioUi';
import { STUDIO_FONT_MONO as FONT_MONO, FONT_SERIF } from '../landing/landingData';
import { fieldLabel } from './videoUi';

// HEIC/HEIF + MOV straight from an iPhone; the server converts HEIC to JPEG.
// Extensions too: desktop pickers do not map .heic/.mov to a MIME everywhere.
const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif,image/heic,image/heif,.heic,.heif,video/mp4,video/webm,video/quicktime,.mov,.m4v';

/**
 * Pick files for a video from the user's media library — the SAME library as the
 * web-app studio's Media tab — or upload new ones (they land in that library too).
 */
export const MediaPicker: React.FC<{ open: boolean; onClose: () => void; onDone: (assets: MediaAsset[]) => void; exclude?: string[] }> = ({ open, onClose, onDone, exclude = [] }) => {
    const [library, setLibrary] = useState<MediaAsset[] | null>(null);
    const [enabled, setEnabled] = useState(true);
    const [picked, setPicked] = useState<string[]>([]);
    const [uploads, setUploads] = useState<{ name: string; progress: number; error?: string }[]>([]);
    const [dragging, setDragging] = useState(false);
    const input = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        setPicked([]);
        setUploads([]);
        mediaService.list().then((r) => {
            setLibrary(r.success ? r.data!.assets : []);
            setEnabled(r.success ? r.data!.enabled !== false : false);
        });
    }, [open]);

    const upload = async (files: FileList | File[]) => {
        const base = uploads.length;
        for (const [n, file] of Array.from(files).entries()) {
            const idx = base + n;
            setUploads((u) => [...u, { name: file.name, progress: 0 }]);
            const r = await mediaService.upload(file, { onProgress: (f) => setUploads((u) => u.map((x, i) => (i === idx ? { ...x, progress: f } : x))) });
            if (r.success && r.data) {
                const asset = r.data;
                setLibrary((l) => [asset, ...(l || [])]);
                setPicked((p) => [...p, asset.assetId]);
                setUploads((u) => u.map((x, i) => (i === idx ? { ...x, progress: 1 } : x)));
            } else {
                setUploads((u) => u.map((x, i) => (i === idx ? { ...x, error: (r as { message?: string }).message || 'Upload gagal' } : x)));
            }
        }
    };

    const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    const items = (library || []).filter((a) => !exclude.includes(a.assetId));
    const busy = uploads.some((u) => u.progress < 1 && !u.error);

    return (
        <Modal open={open} onClose={onClose} maxWidth={720} label="Tambah file">
            <div style={{ padding: 24 }}>
                <div style={{ fontFamily: FONT_SERIF, fontSize: 30, lineHeight: 1 }}>Tambah gambar & video</div>
                <div style={{ marginTop: 6, fontSize: 13, color: 'var(--hv-muted)' }}>File tersimpan di Media library yang sama dengan studio web app-mu.</div>

                {!enabled ? (
                    <div style={{ marginTop: 16, color: 'var(--hv-muted)' }}>Media library belum dikonfigurasi di server.</div>
                ) : (
                    <>
                        <div
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
                            onClick={() => input.current?.click()}
                            style={{ marginTop: 16, padding: '22px 16px', borderRadius: 16, border: `2px dashed ${dragging ? 'var(--brand,#13422C)' : 'var(--hv-line-2)'}`, background: dragging ? 'var(--hv-mint)' : 'var(--hv-page-2)', textAlign: 'center', cursor: 'pointer', fontSize: 14, color: 'var(--hv-body)' }}
                        >
                            <b>Klik atau tarik file ke sini</b> · JPG, PNG, WebP, HEIC, MP4, MOV, WebM
                            <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={(e) => e.target.files && upload(e.target.files)} />
                        </div>
                        {uploads.length > 0 && (
                            <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                                {uploads.map((u, i) => (
                                    <div key={i} style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</span>
                                        {u.error ? <span style={{ color: 'var(--hv-danger)' }}>{u.error}</span> : (
                                            <span style={{ width: 120, height: 6, borderRadius: 99, background: 'var(--hv-page-2)', overflow: 'hidden' }}><span style={{ display: 'block', width: `${Math.round(u.progress * 100)}%`, height: '100%', background: 'var(--hv-leaf)' }} /></span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ ...fieldLabel, marginTop: 18 }}>Dari library</div>
                        {library === null ? <Spinner /> : items.length === 0 ? (
                            <div style={{ fontSize: 13, color: 'var(--hv-muted)' }}>Belum ada file. Unggah di atas.</div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: 10, maxHeight: 300, overflow: 'auto' }}>
                                {items.map((a) => {
                                    const on = picked.includes(a.assetId);
                                    return (
                                        <button key={a.assetId} type="button" onClick={() => toggle(a.assetId)} title={a.originalName} style={{ position: 'relative', padding: 0, borderRadius: 12, overflow: 'hidden', cursor: 'pointer', aspectRatio: '1 / 1', border: `3px solid ${on ? 'var(--brand,#13422C)' : 'transparent'}`, background: 'var(--hv-page-2)' }}>
                                            {a.kind === 'video'
                                                ? <video src={a.url} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                                : <img src={a.url} alt={a.alt || a.originalName} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
                                            <span style={{ position: 'absolute', left: 6, bottom: 6, fontFamily: FONT_MONO, fontSize: 10, padding: '2px 6px', borderRadius: 99, background: 'rgba(0,0,0,.6)', color: '#fff' }}>{a.kind === 'video' ? 'VIDEO' : 'IMG'}</span>
                                            {on && <span style={{ position: 'absolute', right: 6, top: 6, width: 22, height: 22, borderRadius: 99, background: 'var(--brand,#13422C)', color: '#fff', fontSize: 13, display: 'grid', placeItems: 'center' }}>✓</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
                <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <BrandBtn disabled={!picked.length || busy} onClick={() => { onDone((library || []).filter((a) => picked.includes(a.assetId))); onClose(); }}>
                        {busy ? <Spinner light size={14} /> : `Tambahkan${picked.length ? ` (${picked.length})` : ''}`}
                    </BrandBtn>
                </div>
            </div>
        </Modal>
    );
};

/** Small removable thumbnails of attached files. */
export const AttachedChips: React.FC<{ items: MediaAsset[]; onRemove?: (assetId: string) => void }> = ({ items, onRemove }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {items.map((a) => (
            <div key={a.assetId} title={a.originalName} style={{ position: 'relative', width: 54, height: 54, borderRadius: 10, overflow: 'hidden', background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }}>
                {a.kind === 'video'
                    ? <video src={a.url} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <img src={a.url} alt={a.alt || a.originalName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                {a.kind === 'video' && <span style={{ position: 'absolute', left: 3, bottom: 3, fontSize: 9, padding: '1px 4px', borderRadius: 4, background: 'rgba(0,0,0,.6)', color: '#fff' }}>▶</span>}
                {onRemove && (
                    <button type="button" onClick={() => onRemove(a.assetId)} title="Lepas" style={{ position: 'absolute', right: 2, top: 2, width: 18, height: 18, borderRadius: 99, border: 0, background: 'rgba(0,0,0,.65)', color: '#fff', fontSize: 11, lineHeight: '18px', cursor: 'pointer', padding: 0 }}>×</button>
                )}
            </div>
        ))}
    </div>
);
